import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import builderService from "./builderService";
import { logger } from "../utils/logger.js";

const initialState = {
  loading: false,
  builder: {
    pizzaName: "",
    pizzaPrice: 0,
    base: { crust: null, cheeses: [] },
    sauce: {},
    meatTopping: [],
    veggieTopping: [],
    image: [],
  },
  builders: [],
};

// Get Many
export const builderGetMany = createAsyncThunk("builder/getMany", async () => {
  logger.debug("builderGetMany start");
  const response = await builderService.builderGetMany();
  logger.debug("builderGetMany success", response?.data?.count);
  return response.data;
});

// Create
export const builderCreate = createAsyncThunk(
  "builder/create",
  async (builder) => {
    logger.debug("builderCreate", builder?.pizzaName);
    const response = await builderService.builderCreate(builder);
    logger.debug("builderCreate response", response?.pizza?.id || response?.id);
    return response.data;
  }
);

// Get One
export const pizzaGetOne = createAsyncThunk("builder/getOne", async (id) => {
  const response = await builderService.pizzaGetOne(id);
  return response.pizza; // just the pizza object
});

// Update
export const builderUpdateOne = createAsyncThunk(
  "builder/updateOne",
  async (formData, thunkAPI) => {
    try {
      const response = await builderService.builderUpdateOne(formData);
      if (!response.data.success) {
        return thunkAPI.rejectWithValue(response.data.message);
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// Alternative Delete
export const builderDeleteOneAlt = createAsyncThunk(
  "builder/deleteOneAlt",
  async (id) => {
    const response = await builderService.builderDeleteOneAlt(id);
    return response.id; // Just return the id
  }
);

// Toggle Status
export const builderToggleStatus = createAsyncThunk(
  "builder/toggleStatus",
  async ({ id, active }) => {
    const response = await builderService.builderToggleStatus(id, active);
    return response; // full payload includes pizza + builders list
  }
);

export const builderSlice = createSlice({
  name: "builder",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(builderCreate.pending, (state) => {
        logger.debug("builderCreate.pending");
        state.loading = true;
      })
      .addCase(builderCreate.fulfilled, (state, action) => {
        logger.debug("builderCreate.fulfilled");
        state.loading = false;
        // Optimistically add new pizza to list if returned shape matches
        if (action.payload?.pizza) {
          // Normalize id field
          const newPizza = action.payload.pizza;
          if (
            !state.builders.find(
              (p) => p.id === newPizza.id || p._id === newPizza._id
            )
          ) {
            state.builders.push(newPizza);
          }
        }
      })
      .addCase(builderCreate.rejected, (state, action) => {
        logger.warn("builderCreate.rejected", action.payload);
        state.loading = false;
      })

      // Get Many
      .addCase(builderGetMany.pending, (state) => {
        logger.debug("builderGetMany.pending");
        state.loading = true;
      })
      .addCase(builderGetMany.fulfilled, (state, action) => {
        logger.debug("builderGetMany.fulfilled", action.payload?.count);
        state.loading = false;
        // Updates state
        state.builders = action.payload.builders;
      })
      .addCase(builderGetMany.rejected, (state, action) => {
        logger.warn("builderGetMany.rejected", action.payload);
        state.loading = false;
      })

      // Pizza get one
      .addCase(pizzaGetOne.pending, (state) => {
        logger.debug("pizzaGetOne.pending");
        state.loading = true;
      })
      .addCase(pizzaGetOne.fulfilled, (state, action) => {
        if (!action.payload) {
          logger.error("pizzaGetOne.fulfilled missing payload");
          state.loading = false;
          state.builder = null;
          return;
        }
        const builderData = action.payload; // action.payload IS the pizza object
        logger.debug("pizzaGetOne.fulfilled", builderData?.id);
        state.loading = false;
        state.builder = builderData;
      })
      .addCase(pizzaGetOne.rejected, (state, action) => {
        logger.warn("pizzaGetOne.rejected", action.payload);
        state.loading = false;
      })

      // Update
      .addCase(builderUpdateOne.pending, (state) => {
        logger.debug("builderUpdateOne.pending");
        state.loading = true;
      })
      .addCase(builderUpdateOne.fulfilled, (state, action) => {
        logger.debug("builderUpdateOne.fulfilled", action.payload?.builder?.id);
        state.loading = false;
        state.builder = action.payload.builder;
        state.builders = state.builders.map((b) =>
          b.id === action.payload.builder.id ? action.payload.builder : b
        );
      })
      .addCase(builderUpdateOne.rejected, (state, action) => {
        logger.warn("builderUpdateOne.rejected", action.payload);
        state.loading = false;
      })

      // Alternative Delete
      .addCase(builderDeleteOneAlt.fulfilled, (state, action) => {
        const index = state.builders.findIndex(
          (builder) => builder.id === action.payload
        );
        if (index !== -1) {
          state.builders.splice(index, 1);
        }
        state.loading = false;
      })

      // Toggle Status
      .addCase(builderToggleStatus.pending, (state) => {
        logger.debug("builderToggleStatus.pending");
        state.loading = true;
      })
      .addCase(builderToggleStatus.fulfilled, (state, action) => {
        // Diagnostic: log payload summary
        // Replace entire list (mirrors order status update pattern)
        if (action.payload?.builders) {
          state.builders = action.payload.builders;
        } else if (action.payload?.pizza) {
          // Fallback: update single pizza
          const updatedPizza = action.payload.pizza;
          const idx = state.builders.findIndex(
            (b) => b.id === updatedPizza.id || b._id === updatedPizza._id
          );
          if (idx !== -1) state.builders[idx] = updatedPizza;
        }
        state.loading = false;
      })
      .addCase(builderToggleStatus.rejected, (state) => {
        logger.warn("builderToggleStatus.rejected");
        state.loading = false;
      });
  },
});

export default builderSlice.reducer;
